import queryString from 'query-string'

import { Color } from 'services/apiTypes/card.types'
import { DeckHelp } from 'services/apiTypes/deckHelp.types'
import { DeckTagRelation } from 'services/apiTypes/deckTag.types'
import {
  CardType,
  DEPRICATED_FORMAT_NAMES,
  EDH_FORMATS,
  FormatType,
  HUMAN_READABLE_FORMAT,
  StackType,
  DeckPageQueryParams,
} from 'types/deck'

import { deltaToString } from 'utils/Deltas'
import { colorsToColorIdentityName } from 'utils/colorUtils'

export const generateDeckDescription = (
  name: string,
  format: FormatType,
  deckDescription: string,
  deckTags: DeckTagRelation[],
  commanders: CardType[],
  stacks: { mainDeck: StackType[]; sideDeck: StackType[] },
  isSandbox: boolean,
  deckHelpGoals: string | undefined,
  deckHelpIssues: string | undefined,
): { title: string; description: string } => {
  if (isSandbox)
    return {
      title: 'Deck Sandbox - Archidekt',
      description: 'Build a Magic: The Gathering (MTG) deck without an account or needing to save',
    }

  const humanFormat = HUMAN_READABLE_FORMAT[format] || DEPRICATED_FORMAT_NAMES[format]
  const readableDescription = deltaToString(deckDescription)
  const readableTags = getHumanDeckTags(deckTags)
  const commanderNames = commanders.map(card => card.name).join(' & ')
  const stackHeaders = stacks.mainDeck.reduce((acc, stack) => {
    if (!acc) return `(${stack.quantity}) ${stack.name}`

    return `${acc} • (${stack.quantity}) ${stack.name}`
  }, '')

  // @ts-ignore - Object.keys returns strings, it's fine
  const colors: Color[] = Object.keys(
    commanders.reduce(
      (acc, c) => {
        const colors = EDH_FORMATS.includes(format) ? c.colorIdentity : c.colors

        for (const color of colors) {
          acc[color] = true
        }

        return acc
      },
      {} as Record<Color, boolean>,
    ),
  )

  let colorName: string = colorsToColorIdentityName(colors)

  if (colorName !== 'Colorless') {
    if (['White', 'Blue', 'Black', 'Red', 'Green'].includes(colorName)) colorName = `Mono-${colorName}`

    colorName = `${colorName} ` // Adds space for concat later
  } else colorName = '' // Colorless is what returns if we have no colors (which we won't if we have no commanders). AkA - this won't show anything without commanders (which is what I want)

  let title = ''
  let description = ''

  title += name || 'Sandbox'

  if (humanFormat) title += ` • (${colorName}${humanFormat} deck)`
  else title += ` (Card package)`

  // prettier-ignore
  description += `${commanderNames ? `${commanderNames} - ` : ''}${humanFormat ? `${humanFormat} deck` : 'Card Package'} ${readableTags} \n${stackHeaders} \n${readableDescription}`
  title += ` • Archidekt`

  if (deckHelpGoals && deckHelpIssues) {
    const readableGoals = deltaToString(deckHelpGoals)
    const readableIssues = deltaToString(deckHelpIssues)

    // Hard overwriting the page description here
    description = `${commanderNames ? `${commanderNames} - ` : ''}${humanFormat ? `${humanFormat} deck` : 'Card Package'} ${readableTags} \n Deck Goals: \n${readableGoals} \n Deck Issues: \n${readableIssues}\n`
  }

  return { title, description }
}

export const getHumanDeckTags = (deckTags: DeckTagRelation[]) => {
  if (!deckTags.length) return '\n'

  const tagNames = deckTags.map(tag => tag.name)

  return `\n${tagNames.join(' • ')}`
}

// Every run of whitespace, Unicode included, becomes one ASCII space before the
// whitelist runs, so the slug only ever contains [a-z0-9_].
export const convertNameToURLSafeName = (name: string) =>
  name
    .replace(/\s+/g, ' ')
    .replace(/[^A-Za-z0-9 ]/g, '')
    .trim()
    .replace(/  +/g, ' ')
    .replace(/ /g, '_')
    .toLowerCase()

const stringifyDeckPageQueryParams = (params: DeckPageQueryParams) => {
  const queryParams: Record<string, string> = {}

  if (params?.selectedUidCards) queryParams.selectedUids = params.selectedUidCards.map(card => card.uid).join(',')
  if (params?.selectedOracleUidCards)
    queryParams.selectedOracleUids = params.selectedOracleUidCards.map(card => card.oracleCardUid).join(',')

  return `?${queryString.stringify(queryParams)}`
}

export const generateDeckUrl = (id: number | string, name?: string, params?: DeckPageQueryParams) => {
  const paramString = params ? stringifyDeckPageQueryParams(params) : ''
  const slug = name ? convertNameToURLSafeName(name) : ''
  if (!slug) return `/decks/${id}${paramString}`

  return `/decks/${id}/${slug}${paramString}`
}

export const generateSnapshotUrl = (id: number | string, name?: string, params?: DeckPageQueryParams) => {
  const paramString = params ? stringifyDeckPageQueryParams(params) : ''

  const slug = name ? convertNameToURLSafeName(name) : ''
  if (!slug) return `/snapshots/${id}${paramString}`

  return `/snapshots/${id}/${slug}${paramString}`
}
